import { useCallback, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/components/toasts/PlatformToast';

import {
    optimisticallyAddCommentAssociations,
    resourceAssociationQueryKeys,
} from '@/queries/tanstack/database/entities/resourceAssociations';
import { resourceCommentService } from '@/services/database/resourceComments';
import type { ResourceCommentInput } from '@/services/database/resourceComments';
import {
    emitOptimisticResourceCommentCreated,
    emitResourceActivityOpenEvent,
    removeOptimisticResourceComment,
    replaceOptimisticResourceComment,
} from '@/shared/components/activity/resourceCommentEvents';
import { useUserStore, useWorkspaceStore } from '@/stores';
import type { ResourceActivityResourceType, ResourceComment } from '@/types';

interface UseResourceCommentMutationOptions {
    errorMessage?: string;
    onCreated?: (comment: ResourceComment) => void;
    resourceHref?: string;
    resourceId: string;
    resourceTitle?: string;
    resourceType: ResourceActivityResourceType;
    resourceWebsite?: string;
}

interface ResourceCommentMutationCallbacks {
    onError?: (error: unknown, optimisticComment: ResourceComment) => void;
    onOptimisticCreated?: (comment: ResourceComment) => void;
    onPersisted?: (comment: ResourceComment) => void;
}

export const useResourceCommentMutation = ({
    errorMessage = 'Could not create comment',
    onCreated,
    resourceHref,
    resourceId,
    resourceTitle,
    resourceType,
    resourceWebsite,
}: UseResourceCommentMutationOptions) => {
    const queryClient = useQueryClient();
    const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspace?.id);
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const workspaceId = selectedWorkspaceId ?? activeWorkspaceId;
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const createComment = useCallback(
        async (input: ResourceCommentInput, callbacks: ResourceCommentMutationCallbacks = {}) => {
            if (!resourceId || submittingRef.current) {
                return undefined;
            }

            submittingRef.current = true;
            setSubmitting(true);

            const optimisticComment = emitOptimisticResourceCommentCreated(resourceType, resourceId, input);
            const rollbackAssociations = optimisticallyAddCommentAssociations({
                commentId: optimisticComment.id,
                component: input.component,
                mentions: input.mentions,
                queryClient,
                source: {
                    href: resourceHref,
                    id: resourceId,
                    label: resourceTitle ?? resourceId,
                    resourceType,
                    website: resourceWebsite,
                },
                workspaceId,
            });

            callbacks.onOptimisticCreated?.(optimisticComment);

            emitResourceActivityOpenEvent({
                commentId: optimisticComment.id,
                resourceId,
                resourceType,
                tab: 'comments',
            });

            try {
                const persistedComment = await resourceCommentService.create(resourceType, resourceId, input);

                replaceOptimisticResourceComment(optimisticComment, persistedComment, resourceId, resourceType);
                emitResourceActivityOpenEvent({
                    commentId: persistedComment.id,
                    resourceId,
                    resourceType,
                    tab: 'comments',
                });
                onCreated?.(persistedComment);
                callbacks.onPersisted?.(persistedComment);
                void queryClient.invalidateQueries({ queryKey: resourceAssociationQueryKeys.all });

                return persistedComment;
            } catch (error) {
                rollbackAssociations();
                removeOptimisticResourceComment(optimisticComment, resourceId, resourceType);
                callbacks.onError?.(error, optimisticComment);
                captureException(error, { extra: { resourceId, resourceType } });
                toast.error(errorMessage);

                return undefined;
            } finally {
                submittingRef.current = false;
                setSubmitting(false);
            }
        },
        [
            errorMessage,
            onCreated,
            queryClient,
            resourceHref,
            resourceId,
            resourceTitle,
            resourceType,
            resourceWebsite,
            workspaceId,
        ],
    );

    return { createComment, submitting };
};
